import {
  SurveySessionCodes,
  SurveySessionMessages,
} from '@chiroup/core/constants/HealthComSurveySession';
import * as Sentry from '@sentry/react';

/**
 * Shared recovery for whole-survey submits (CCS family + re-eval) that fail
 * because the copy was already completed or superseded by a re-send.
 * Returns true when the error was handled (patient re-routed), false when
 * the caller should run its normal error path.
 */
export const recoverStaleSurveySession = ({
  err,
  kiosk,
  language,
  currentQuestionId,
}: {
  err: unknown;
  kiosk?: boolean;
  language?: string;
  currentQuestionId?: string | null;
}): boolean => {
  const errorData = (err as any)?.response?.data;
  const errMessage = errorData?.message || (err as any)?.message;
  const replacementRequestId = errorData?.details?.replacementRequestId;

  const isAlreadyCompleted =
    errorData?.details?.code === SurveySessionCodes.SURVEY_ALREADY_COMPLETED_CODE ||
    errMessage === SurveySessionMessages.SURVEY_ALREADY_COMPLETED_MESSAGE;
  const isSuperseded =
    errorData?.details?.code === SurveySessionCodes.SURVEY_SUPERSEDED_CODE ||
    errMessage === SurveySessionMessages.SURVEY_SUPERSEDED_MESSAGE;
  const isExpired =
    errorData?.details?.code === SurveySessionCodes.REQUEST_EXPIRED_CODE ||
    errMessage === SurveySessionMessages.REQUEST_EXPIRED_MESSAGE;

  if (!isAlreadyCompleted && !isSuperseded && !isExpired) {
    return false;
  }

  // Expired: the request was expired (staff action, reschedule, re-send)
  // after this session loaded its steps. Nothing to forward — tell the
  // patient and route them out of the dead session.
  if (isExpired) {
    alert(
      language === 'es'
        ? 'Esta solicitud ya no está disponible. Comuníquese con su clínica si aún necesita completarla.'
        : 'This request is no longer available. Please contact your clinic if you still need to complete it.'
    );

    Sentry.logger.info('Recovering stale survey session: expired request', {
      currentQuestionId: currentQuestionId || null,
      kiosk: !!kiosk,
    });

    if (kiosk) {
      window.location.assign('/kiosk');
    } else {
      window.location.reload();
    }

    return true;
  }

  // Superseded: the survey template changed between sends, so the
  // answers can't be carried over — tell the patient before routing
  // them to the replacement (kiosk re-entry serves the new copy).
  if (isSuperseded) {
    alert(
      language === 'es'
        ? 'Esta encuesta fue actualizada. Por favor complete la nueva versión.'
        : 'This survey was updated. Please complete the new version.'
    );

    Sentry.logger.info('Recovering stale survey session: superseded survey', {
      replacementRequestId: replacementRequestId || null,
      currentQuestionId: currentQuestionId || null,
      kiosk: !!kiosk,
    });

    if (kiosk) {
      window.location.assign('/kiosk');
    } else if (replacementRequestId) {
      window.location.assign(`/requests/${replacementRequestId}`);
    } else {
      window.location.reload();
    }

    return true;
  }

  // Already completed (genuine):
  //  kiosk -> back to the keypad;
  //  replacement known -> that request's page;
  //  otherwise reload.
  if (kiosk) {
    Sentry.logger.info('Recovering stale survey session: kiosk redirect', {
      replacementRequestId: replacementRequestId || null,
      currentQuestionId: currentQuestionId || null,
    });

    window.location.assign('/kiosk');

    return true;
  } else if (replacementRequestId) {
    Sentry.logger.info('Recovering stale survey session: replacement request redirect', {
      replacementRequestId,
      currentQuestionId: currentQuestionId || null,
    });

    window.location.assign(`/requests/${replacementRequestId}`);

    return true;
  } else {
    Sentry.logger.info('Recovering stale survey session: fallback request redirect', {
      replacementRequestId: null,
      currentQuestionId: currentQuestionId || null,
    });

    window.location.reload();

    return true;
  }
};
